// Deck settings modal — owns every deck-scoped preference, so that
// switching decks switches the whole panel's contents at once.
//
// Sections (all per-deck CONFIG, in display order):
//   1. Lang-pair (J'apprends / Depuis)     — bilingual only
//   2. Direction slider (Sens de traduction)
//   3. Difficulty pill row
//   4. Tag filter (class / domain / register, or mono-tag axis)
//   5. TTS master + autoplay                — defaults per shape
//
// Two entry points open this modal (stats/mastery moved to their own
// deckStatsModal in a later commit — "data" and "settings" are
// conceptually distinct):
//   - ⚙ under the card (primary, always visible while playing)
//   - ⚙ in the hamburger (fallback for empty / loading card states)
// The "niv" pill now opens deckStatsModal instead of this one.
//
// Close guards are intentionally simpler than user-modal's — none of
// the deck settings can be left in an invalid state.

import type { CardList } from "../config/lists";
import { t } from "../i18n";
import { byId, hide, show } from "./dom";

export type DeckSettingsDeps = {
  /** Active deck — used to render the modal title, branch rendering
   *  on `shape` (bilingual/mono), and resolve per-deck defaults. */
  getList: () => CardList;
  /** Re-render the lang-pair + direction controls on modal open. */
  renderLangControls: () => void;
  /** Re-render the difficulty pill row on open. */
  renderDiffDropdown: () => void;
  /** Re-render the tag filter sections on open. */
  renderTagFilterDropdown: () => void;
  /** TTS config getters/setters. Enabled gates the ♪ speaker + any
   *  TTS firing; autoplay is only consulted when enabled. Changes
   *  persist to the per-deck TTS_CONFIG bag and take effect on the
   *  NEXT rendered card (which is fine — the modal is usually closed
   *  before the user sees another card). */
  getTtsEnabled: () => boolean;
  setTtsEnabled: (v: boolean) => void;
  getTtsAutoplay: () => boolean;
  setTtsAutoplay: (v: boolean) => void;
};

export type DeckSettings = {
  open: () => void;
  close: () => void;
  /** Apply current locale to modal copy. Safe to call before open. */
  localize: () => void;
};

export function createDeckSettings(deps: DeckSettingsDeps): DeckSettings {
  function localize(): void {
    const closeBtn = byId<HTMLButtonElement>("deckSettingsCloseBtn");
    if (closeBtn) closeBtn.textContent = t("deck_settings_close") as string;
  }

  function renderTitle(): void {
    const titleEl = byId("deckSettingsModalTitle");
    const subEl = byId("deckSettingsModalSub");
    const list = deps.getList();
    const deckName = t(list.nameKey) as string;
    if (titleEl) {
      titleEl.textContent =
        (t("deck_settings_title_prefix") as string) + deckName;
    }
    if (subEl) {
      subEl.textContent = t("deck_settings_sub") as string;
    }
  }

  /** Sync the TTS checkbox pair with current state + wire onchange.
   *  The autoplay checkbox is disabled when master is off — an
   *  autoplay-on-but-audio-off state is meaningless. */
  function syncTtsSection(): void {
    const masterCb = byId<HTMLInputElement>("deckTtsEnabled");
    const autoplayCb = byId<HTMLInputElement>("deckTtsAutoplay");
    if (!masterCb || !autoplayCb) return;
    masterCb.checked = deps.getTtsEnabled();
    autoplayCb.checked = deps.getTtsAutoplay();
    autoplayCb.disabled = !masterCb.checked;
    masterCb.onchange = () => {
      deps.setTtsEnabled(masterCb.checked);
      autoplayCb.disabled = !masterCb.checked;
      // Turning master off also visually unchecks autoplay so the user
      // sees what's actually active — the stored state still remembers
      // their autoplay preference, so re-enabling master restores it.
      if (!masterCb.checked) autoplayCb.checked = false;
    };
    autoplayCb.onchange = () => {
      deps.setTtsAutoplay(autoplayCb.checked);
    };
  }

  function localizeInlineLabels(): void {
    const diffLabel = byId("modalDiffLabel");
    if (diffLabel) diffLabel.textContent = t("menu_label_diff") as string;
    const filterLabel = byId("modalFilterLabel");
    if (filterLabel) filterLabel.textContent = t("menu_label_filter") as string;
    const ttsSectionLabel = byId("modalTtsSectionLabel");
    if (ttsSectionLabel) {
      ttsSectionLabel.textContent = t("deck_settings_audio_label") as string;
    }
    const ttsEnabledLabel = byId("deckTtsEnabledLabel");
    if (ttsEnabledLabel) {
      ttsEnabledLabel.textContent = t("deck_settings_tts_enabled") as string;
    }
    const ttsAutoplayLabel = byId("deckTtsAutoplayLabel");
    if (ttsAutoplayLabel) {
      ttsAutoplayLabel.textContent = t("deck_settings_tts_autoplay") as string;
    }
  }

  function open(): void {
    localize();
    renderTitle();
    // Each section keeps its own renderer — call them all on open so
    // the modal paints consistent state regardless of what triggered
    // it (deck switch, locale change, etc. already fire these, but
    // re-running here makes open() self-contained).
    deps.renderLangControls();
    deps.renderDiffDropdown();
    deps.renderTagFilterDropdown();
    syncTtsSection();
    localizeInlineLabels();
    show(byId("deckSettingsModal"));
  }

  function close(): void {
    hide(byId("deckSettingsModal"));
  }

  return { open, close, localize };
}

/** Wire close button + backdrop click. Call once on boot. */
export function installDeckSettingsModal(onClose: () => void): void {
  const modal = byId("deckSettingsModal");
  const closeBtn = byId("deckSettingsCloseBtn");
  closeBtn?.addEventListener("click", onClose);
  modal?.addEventListener("click", (e) => {
    if (e.target === modal) onClose();
  });
}
